import { useState } from "react";
import { Drawer } from "../Drawer";
import { Icon } from "../ui/Icon";
import { OrderAttachmentPanel } from "../attachments/OrderAttachmentPanel";
import { trpc } from "../../lib/trpc";

interface TraceViewerProps {
  open: boolean;
  lotId: string | null;
  lotCode?: string;
  onClose: () => void;
}

interface TraceNode {
  lotId: string;
  lotCode: string;
  itemKind: "stock" | "semi" | "finished";
  itemCode: string;
  itemName: string;
  qty: number;
  uom: string;
  woCode: string | null;
  supplierLot: string | null;
  status: string;
  children: TraceNode[];
}

interface ConsumptionNode {
  itemKind: "stock" | "semi" | "finished";
  itemCode: string;
  itemName: string;
  uom: string;
  lotId: string | null;
  lotCode: string | null;
  neededQty: number | null;
  consumedQty: number | null;
  isRaw: boolean;
  recorded: boolean;
  status: string | null;
  supplierLot: string | null;
  expiresAt: string | null;
  source: string;
  sourceCode: string | null;
  woCode: string | null;
  batchCode: string | null;
  lineName: string | null;
  recordedBy: string | null;
  supplierName: string | null;
  producedAt: string | null;
  children: ConsumptionNode[];
}

function kindMeta(kind: string): { label: string; color: string } {
  return kind === "stock" ? { label: "HM", color: "var(--neutral-500)" }
    : kind === "semi" ? { label: "YM", color: "var(--info)" }
    : { label: "MM", color: "var(--success)" };
}
function fmtQty(n: number | null): string {
  if (n == null) return "—";
  return new Intl.NumberFormat("tr-TR", { maximumFractionDigits: 2 }).format(n);
}
function fmtDate(d: string | null): string {
  if (!d) return "—";
  return new Intl.DateTimeFormat("tr-TR", { day: "2-digit", month: "2-digit", year: "numeric" }).format(new Date(d));
}
const STATUS_TR: Record<string, string> = {
  released: "Serbest", pending_qc: "QC bekliyor", on_hold: "Bloke", rejected: "Reddedildi", expired: "SKT geçti", consumed: "Tüketildi",
};

// ── PDF (yazdırılabilir) izlenebilirlik raporu — irsaliye deseni (window.open + print) ──
const KIND_LABEL: Record<string, string> = { stock: "HM", semi: "YM", finished: "MM" };
const KIND_HEX: Record<string, string> = { stock: "#71717a", semi: "#2563eb", finished: "#15803d" };
function escHtml(s: string): string {
  return s.replace(/[&<>"]/g, (c) => ({ "&": "&amp;", "<": "&lt;", ">": "&gt;", '"': "&quot;" }[c] ?? c));
}
function fmtDateTime(d: Date): string {
  return new Intl.DateTimeFormat("tr-TR", { day: "2-digit", month: "2-digit", year: "numeric", hour: "2-digit", minute: "2-digit" }).format(d);
}
function nodeRowsHtml(node: ConsumptionNode, depth: number): string {
  const label = KIND_LABEL[node.itemKind] ?? "?";
  const hex = KIND_HEX[node.itemKind] ?? "#71717a";
  const qty = node.consumedQty ?? node.neededQty;
  const meta: string[] = [];
  if (!node.recorded) meta.push('<span style="color:#b45309;font-weight:600">Fiili kayıt yok (teorik)</span>');
  if (node.batchCode) meta.push(`Parti: <b>${escHtml(node.batchCode)}</b>`);
  if (node.woCode) meta.push(`İş Emri: <b>${escHtml(node.woCode)}</b>`);
  if (node.lineName) meta.push(`Hat: <b>${escHtml(node.lineName)}</b>`);
  if (node.recordedBy) meta.push(`Kaydeden: <b>${escHtml(node.recordedBy)}</b>`);
  if (node.producedAt) meta.push(`Tarih: <b>${fmtDate(node.producedAt)}</b>`);
  if (node.supplierName) meta.push(`Tedarikçi: <b>${escHtml(node.supplierName)}${node.supplierLot ? ` (${escHtml(node.supplierLot)})` : ""}</b>`);
  if (node.recorded && node.status) meta.push(`Durum: <b>${STATUS_TR[node.status] ?? node.status}</b>`);
  if (node.expiresAt) meta.push(`SKT: <b>${fmtDate(node.expiresAt)}</b>`);
  const lotDisp = node.lotCode
    ? `<span style="font-family:monospace;font-weight:600">${escHtml(node.lotCode)}</span>`
    : `<span style="font-style:italic;color:#b45309">reçeteye göre</span>`;
  const row = `
    <div class="node" style="margin-left:${depth * 20}px">
      <div class="node-main">
        <span class="badge" style="color:${hex}">${label}</span>
        ${lotDisp}
        <span class="item">${escHtml(node.itemName)} <span class="code">${escHtml(node.itemCode)}</span></span>
        <span class="qty">${fmtQty(qty)} ${escHtml(node.uom)}</span>
      </div>
      ${meta.length ? `<div class="meta">${meta.join(" &middot; ")}</div>` : ""}
    </div>`;
  return row + node.children.map((c) => nodeRowsHtml(c, depth + 1)).join("");
}
function printTraceReport(tree: ConsumptionNode, lotCode: string, generatedBy: string) {
  const w = window.open("", "_blank", "width=900,height=1000");
  if (!w) return;
  const now = fmtDateTime(new Date());
  w.document.write(`<!doctype html><html lang="tr"><head><meta charset="utf-8"><title>İzlenebilirlik Raporu ${escHtml(lotCode)}</title>
    <style>
    body{font-family:'Segoe UI',Arial,sans-serif;color:#0a0a0a;padding:32px;font-size:12px}
    .head{display:flex;justify-content:space-between;align-items:flex-start;border-bottom:2px solid #14253e;padding-bottom:14px;margin-bottom:8px}
    .brand{font-size:22px;font-weight:700;letter-spacing:0.1em;color:#14253e}
    .doc{font-size:12px;color:#71717a;margin-top:2px}
    h1{font-size:18px;margin:0}
    .sub{color:#71717a;font-size:11px}
    .gen{font-size:10.5px;color:#71717a;margin-bottom:14px}
    .legend{font-size:10px;color:#71717a;margin-bottom:10px}
    .node{padding:6px 0 7px;border-bottom:1px solid #ececec;break-inside:avoid;page-break-inside:avoid}
    .node-main{display:flex;align-items:center;gap:8px}
    .badge{font-size:9px;font-weight:700;border:1px solid currentColor;border-radius:4px;padding:0 4px;flex-shrink:0}
    .item{flex:1;color:#27272a}
    .code{font-family:monospace;color:#a1a1aa;font-size:10px}
    .qty{font-family:monospace;font-weight:600;color:#3f3f46;flex-shrink:0}
    .meta{font-size:10px;color:#71717a;margin-top:3px;padding-left:30px}
    .sig{margin-top:40px;display:flex;justify-content:space-between}
    .sig div{width:45%;border-top:1px solid #0a0a0a;padding-top:6px;font-size:10.5px;text-align:center;color:#71717a}
    .foot{margin-top:22px;font-size:9.5px;color:#a1a1aa;text-align:center}
    @media print{body{padding:0}}
    </style></head><body>
    <div class="head">
      <div><div class="brand">STEPERP</div><div class="doc">İzlenebilirlik Raporu — Yönetici Dokümanı</div></div>
      <div style="text-align:right"><h1>${escHtml(lotCode)}</h1><div class="sub">${escHtml(tree.itemName)}</div></div>
    </div>
    <div class="gen">Oluşturulma: <b>${now}</b>${generatedBy ? ` &middot; Oluşturan: <b>${escHtml(generatedBy)}</b>` : ""}</div>
    <div class="legend">HM = Hammadde &middot; YM = Yarı Mamul &middot; MM = Mamul &middot; "reçeteye göre" = fiili tüketim kaydı olmayan teorik bileşen</div>
    ${nodeRowsHtml(tree, 0)}
    <div class="sig"><div>Hazırlayan</div><div>Onaylayan</div></div>
    <div class="foot">Bu belge STEPERP tarafından otomatik üretilmiştir.</div>
    </body></html>`);
  w.document.close();
  setTimeout(() => w.print(), 350);
}

// ── İleriye (etki) ağacı ─────────────────────────────────────────────────
function TraceTreeNode({ node, depth }: { node: TraceNode; depth: number }) {
  const km = kindMeta(node.itemKind);
  return (
    <div>
      <div style={{ display: "flex", alignItems: "center", gap: 8, padding: "8px 10px", paddingLeft: 10 + depth * 22, borderBottom: "1px solid var(--neutral-100)", fontSize: 12.5 }}>
        <span style={{ fontSize: 9.5, fontWeight: 600, color: km.color, background: "var(--neutral-100)", padding: "1px 5px", borderRadius: 4, flexShrink: 0 }}>{km.label}</span>
        <span className="mono" style={{ fontSize: 11.5, fontWeight: 500, color: "var(--neutral-900)", flexShrink: 0 }}>{node.lotCode}</span>
        <span style={{ color: "var(--neutral-700)", flex: 1, minWidth: 0, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
          {node.itemName}
          {node.woCode ? <span className="mono" style={{ color: "var(--info)", fontSize: 10.5, marginLeft: 6 }}>←{node.woCode}</span> : null}
          {node.supplierLot ? <span style={{ color: "var(--neutral-400)", fontSize: 10.5, marginLeft: 6 }}>tedarikçi: {node.supplierLot}</span> : null}
        </span>
        <span className="mono" style={{ fontSize: 11, color: "var(--neutral-500)", flexShrink: 0 }}>{new Intl.NumberFormat("tr-TR", { maximumFractionDigits: 1 }).format(node.qty)} {node.uom}</span>
      </div>
      {node.children.map((c) => <TraceTreeNode key={c.lotId + depth} node={c} depth={depth + 1} />)}
    </div>
  );
}

// ── Üretim serüveni (geriye) ağacı ────────────────────────────────────────
function MetaChip({ icon, label, value, color }: { icon: string; label: string; value: string; color?: string }) {
  return (
    <span style={{ display: "inline-flex", alignItems: "center", gap: 3, fontSize: 10.5, color: color ?? "var(--neutral-500)" }}>
      <Icon name={icon} size={11} />
      <span style={{ color: "var(--neutral-400)" }}>{label}:</span>
      <span style={{ color: color ?? "var(--neutral-600)", fontWeight: 500 }}>{value}</span>
    </span>
  );
}

function ConsumptionTreeNode({ node, depth }: { node: ConsumptionNode; depth: number }) {
  const km = kindMeta(node.itemKind);
  const qtyShown = node.consumedQty ?? node.neededQty;
  const showNeeded = node.recorded && node.consumedQty != null && node.neededQty != null && Math.abs(node.neededQty - node.consumedQty) > 0.01;
  return (
    <div>
      <div style={{ padding: "8px 10px 9px", paddingLeft: 10 + depth * 20, borderBottom: "1px solid var(--neutral-100)", borderLeft: depth > 0 ? "2px solid var(--neutral-100)" : undefined }}>
        <div style={{ display: "flex", alignItems: "center", gap: 8, fontSize: 12.5 }}>
          <span style={{ fontSize: 9.5, fontWeight: 600, color: km.color, background: "var(--neutral-100)", padding: "1px 5px", borderRadius: 4, flexShrink: 0 }}>{km.label}</span>
          {node.lotCode
            ? <span className="mono" style={{ fontSize: 11.5, fontWeight: 600, color: "var(--neutral-900)", flexShrink: 0 }}>{node.lotCode}</span>
            : <span style={{ fontSize: 10.5, fontStyle: "italic", color: "var(--warning)", flexShrink: 0 }}>reçeteye göre</span>}
          <span style={{ color: "var(--neutral-800)", flex: 1, minWidth: 0, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
            {node.itemName}<span className="mono" style={{ color: "var(--neutral-400)", fontSize: 10.5, marginLeft: 6 }}>{node.itemCode}</span>
          </span>
          <span className="mono" style={{ fontSize: 11.5, color: "var(--neutral-700)", flexShrink: 0, fontWeight: 500 }}>
            {fmtQty(qtyShown)} {node.uom}
            {showNeeded ? <span style={{ color: "var(--neutral-400)", fontWeight: 400 }}> / ger. {fmtQty(node.neededQty)}</span> : null}
          </span>
        </div>
        {/* Üretim serüveni metadata satırı */}
        <div style={{ display: "flex", flexWrap: "wrap", gap: "2px 12px", marginTop: 4, paddingLeft: 26 }}>
          {!node.recorded ? <span style={{ fontSize: 10.5, color: "var(--warning)", fontWeight: 500 }}>Fiili tüketim kaydı yok (teorik)</span> : null}
          {node.batchCode ? <MetaChip icon="package" label="Parti" value={node.batchCode} /> : null}
          {node.woCode ? <MetaChip icon="fileText" label="İş Emri" value={node.woCode} color="var(--info)" /> : null}
          {node.lineName ? <MetaChip icon="factory" label="Hat" value={node.lineName} /> : null}
          {node.recordedBy ? <MetaChip icon="users" label="Kaydeden" value={node.recordedBy} /> : null}
          {node.producedAt ? <MetaChip icon="clock" label="Tarih" value={fmtDate(node.producedAt)} /> : null}
          {node.supplierName ? <MetaChip icon="truck" label="Tedarikçi" value={node.supplierLot ? `${node.supplierName} (${node.supplierLot})` : node.supplierName} /> : null}
          {node.recorded && node.status ? <MetaChip icon="checkCircle" label="Durum" value={STATUS_TR[node.status] ?? node.status} color={node.status === "rejected" || node.status === "on_hold" ? "var(--danger)" : node.status === "released" ? "var(--success)" : "var(--warning)"} /> : null}
          {node.expiresAt ? <MetaChip icon="clock" label="SKT" value={fmtDate(node.expiresAt)} /> : null}
        </div>
      </div>
      {node.children.map((c, i) => <ConsumptionTreeNode key={(c.lotId ?? c.itemCode) + "-" + depth + "-" + i} node={c} depth={depth + 1} />)}
    </div>
  );
}

export function TraceViewerDrawer({ open, lotId, lotCode, onClose }: TraceViewerProps) {
  const [dir, setDir] = useState<"backward" | "forward">("backward");
  const meQ = trpc.auth.me.useQuery(undefined, { staleTime: 5 * 60_000 });
  /**
   * "Üretim serüveni (geriye)" = reçete kırılımı → MAHREM: `view:recipe` izni gerekir.
   * Uçtaki tek tanımın aynası (apps/api/src/auth/permission.ts → requireReceteGorme);
   * `qc.traceConsumption` aynı izni arar. İleriye izleme (recall) HERKESE açıktır —
   * kırılım göstermez, gıda geri-çağırmasında da engellenmemelidir.
   * ⚠ `isSuperAdmin` DEĞİL: tesise bağlı Sistem Yöneticisi de görebilmeli (31 Ağu 2026).
   */
  const receteGorunur = !!meQ.data?.user && (!!meQ.data.user.isSuperAdmin || (meQ.data.user.permissions ?? []).includes("view:recipe"));
  const effDir = receteGorunur ? dir : "forward";

  const consQ = trpc.qc.traceConsumption.useQuery({ lotId: lotId! }, { enabled: open && !!lotId && effDir === "backward" });
  const fwdQ = trpc.qc.traceForward.useQuery({ lotId: lotId! }, { enabled: open && !!lotId && effDir === "forward" });
  const consTree = consQ.data as ConsumptionNode | null | undefined;
  const fwdTree = fwdQ.data as TraceNode | null | undefined;
  const activeQ = effDir === "backward" ? consQ : fwdQ;
  const generatedBy = meQ.data?.user ? `${meQ.data.user.firstName} ${meQ.data.user.lastName}`.trim() : "";

  return (
    <Drawer open={open} onClose={onClose} size="lg" title="İzlenebilirlik (Trace)" subtitle={lotCode ? `Lot: ${lotCode}` : undefined}>
      <div className="form-radio-group" role="radiogroup" style={{ marginBottom: 14 }}>
        {receteGorunur ? (
          <label className="form-radio">
            <input type="radio" name="dir" checked={dir === "backward"} onChange={() => setDir("backward")} />
            <span style={{ display: "inline-flex", alignItems: "center", gap: 5 }}><Icon name="arrowLeft" size={14} />Üretim serüveni (geriye)</span>
          </label>
        ) : null}
        <label className="form-radio">
          <input type="radio" name="dir" checked={effDir === "forward"} onChange={() => setDir("forward")} />
          <span style={{ display: "inline-flex", alignItems: "center", gap: 5 }}><Icon name="arrowRight" size={14} />İleriye (mamule)</span>
        </label>
      </div>

      {!receteGorunur ? (
        <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 12, padding: "9px 12px", background: "var(--neutral-50)", border: "1px dashed var(--neutral-300)", borderRadius: "var(--radius-md)", fontSize: 11.5, color: "var(--neutral-500)" }}>
          <Icon name="lock" size={13} />Üretim serüveni (geriye kırılım) mahremdir — “Reçete kırılımını görüntüle” (view:recipe) izni gerekir. Yalnızca ileriye (recall) izleme kullanılabilir.
        </div>
      ) : null}

      <div style={{ fontSize: 11.5, color: "var(--neutral-500)", marginBottom: 12 }}>
        {effDir === "backward"
          ? "Bu ürün hangi stok/yarı mamullerden üretildi — parti/lot numaraları, üretim hattı, kaydı alan kullanıcı ve tarihe kadar tam kırılım."
          : "Bu lot hangi mamullere dönüştü? (recall: etki izleme — kontaminasyon yayılımı)"}
      </div>

      {/* Mal kabul belge/görselleri — bu lota bağlı ekler EN ÜSTTE (her yön + hammadde lotu için görünür). */}
      {lotId ? (
        <OrderAttachmentPanel
          lotId={lotId}
          canEdit={false}
          uploadCategory="receipt"
          title="Belgeler & Görseller"
          hideWhenEmpty
        />
      ) : null}

      {activeQ.isLoading ? (
        <div style={{ padding: 32, textAlign: "center", color: "var(--neutral-500)" }}>İzleniyor…</div>
      ) : effDir === "backward" ? (
        !consTree ? (
          <div style={{ padding: 32, textAlign: "center", color: "var(--neutral-500)" }}>Bu lot bulunamadı.</div>
        ) : consTree.itemKind === "stock" ? (
          <div style={{ padding: 32, textAlign: "center", color: "var(--neutral-500)" }}>Bu lot bir hammadde (mal kabul) — üretim kırılımı yok.</div>
        ) : (
          <>
            <div style={{ display: "flex", justifyContent: "flex-end", marginBottom: 8 }}>
              <button type="button" className="btn-md secondary" onClick={() => printTraceReport(consTree, lotCode ?? consTree.lotCode ?? "", generatedBy)}>
                <Icon name="printer" size={15} />PDF Raporu
              </button>
            </div>
            <div className="d-card">
              <div className="d-card-head">
                <div className="left"><span className="t">Üretim Serüveni</span></div>
              </div>
              <div className="d-card-body flush">
                <ConsumptionTreeNode node={consTree} depth={0} />
              </div>
            </div>
          </>
        )
      ) : !fwdTree ? (
        <div style={{ padding: 32, textAlign: "center", color: "var(--neutral-500)" }}>Bu lot henüz bir üretimde tüketilmemiş.</div>
      ) : (
        <div className="d-card">
          <div className="d-card-head">
            <div className="left"><span className="t">Etki Ağacı</span></div>
          </div>
          <div className="d-card-body flush">
            <TraceTreeNode node={fwdTree} depth={0} />
          </div>
        </div>
      )}
    </Drawer>
  );
}
